import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { TbBuildingCommunity, TbUsers, TbFileSpreadsheet, TbSignature, TbDatabase, TbTargetArrow, TbChartBar } from "react-icons/tb";
import { availabilityDetail, moneyShort, summarizeUnits } from "./unitStats";
import AvailabilityPopover from "./AvailabilityPopover";
import "./portfolioTable.css";

const OPEN_DELAY = 150;
const CLOSE_DELAY = 200;

/**
 * Open-on-hover, stay-open-on-click for the Occupancy, Renewals and Renewed / Rented tiles. Hover opens
 * after a short pause (so sweeping the mouse across the row does not flash popovers);
 * a click pins it until the next click, Escape, or a click elsewhere.
 */
function useTilePopover() {
  const [open, setOpen] = useState(null);
  const [pinned, setPinned] = useState(false);
  const timer = useRef(null);
  const rootRef = useRef(null);

  const later = (fn, ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fn, ms);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(null);
        setPinned(false);
      }
    };
    const onDown = (e) => {
      if (pinned && rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(null);
        setPinned(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, pinned]);

  const tileProps = (kind) => ({
    role: "button",
    tabIndex: 0,
    "aria-expanded": open === kind,
    "aria-haspopup": "dialog",
    onMouseEnter: () => {
      if (pinned) return;
      later(() => setOpen(kind), open ? 0 : OPEN_DELAY);
    },
    onMouseLeave: () => {
      if (!pinned) later(() => setOpen(null), CLOSE_DELAY);
    },
    onClick: (e) => {
      // Clicks inside the open popover (scrolling, selecting text) never close it.
      if (e.target.closest(".avp")) return;
      clearTimeout(timer.current);
      if (pinned && open === kind) {
        setPinned(false);
        setOpen(null);
      } else {
        setPinned(true);
        setOpen(kind);
      }
    },
    onKeyDown: (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      const closing = open === kind;
      setPinned(!closing);
      setOpen(closing ? null : kind);
    },
  });

  return { open, pinned, rootRef, tileProps };
}

const PART_CLASS = ["pft-split__a", "pft-split__b", "pft-split__c"];

const StatIcon = ({ icon: Icon }) => (
  <span className="pft-stat__icon" aria-hidden="true"><Icon focusable="false" /></span>
);

/** "1517 / 35", "44 / 7 / 25" — the first figure leads, the others read as its counterparts. */
const Split = ({ parts }) => (
  <span className="pft-split">
    {parts.map((n, i) => (
      <Fragment key={PART_CLASS[i]}>
        {i > 0 && <span className="pft-split__sep" aria-hidden="true">/</span>}
        <span className={PART_CLASS[i]}>{n}</span>
      </Fragment>
    ))}
  </span>
);

/**
 * The summary tiles every mastersheet view shows above its table, worded as the owner's
 * sheet reads (2026-10-01): Current Units · Occupancy / Current Vacancy · Renewals till {end
 * of next month} · Renewed / Rented / Pending (needs each row's `signed_lease` flag) · basic
 * rent, target rent, gap to target. The three middle tiles open their unit-by-unit detail
 * on hover; a click pins it. `rows` is what is currently visible; `totalRows` is the
 * unfiltered count so the Units tile can read "12 of 345" while a filter narrows the view.
 */
const MastersheetStats = ({ rows, totalRows, propertyCount, className = "", referenceDate }) => {
  // `referenceDate` (Historic sheet): the next-month window counts from that day, not today.
  const t = useMemo(() => summarizeUnits(rows, referenceDate), [rows, referenceDate]);
  const total = totalRows ?? rows.length;
  const pop = useTilePopover();
  // Built only while a popover is open: the lists cost nothing until someone asks.
  const detail = useMemo(() => (pop.open ? availabilityDetail(rows, referenceDate) : null), [pop.open, rows, referenceDate]);
  const popover = (kind) =>
    pop.open === kind && detail ? <AvailabilityPopover kind={kind} detail={detail} stats={t} hasRentedInfo={t.hasRentedInfo} /> : null;
  const popTile = (kind, tone) => ({
    className: `pft-stat pft-stat--${tone} pft-stat--pop ${pop.open === kind ? "is-open" : ""}`,
    ...pop.tileProps(kind),
  });

  let unitsSub;
  if (rows.length !== total) unitsSub = `of ${total} · filtered`;
  else if (propertyCount !== undefined && propertyCount !== null) unitsSub = `across ${propertyCount} propert${propertyCount === 1 ? "y" : "ies"}`;
  else unitsSub = "on this sheet";

  const win = t.window;

  return (
    <div className={`pft-stats ${className}`.trim()} data-testid="mastersheet-stats" ref={pop.rootRef}>
      <div className="pft-stat" data-testid="stat-units">
        <StatIcon icon={TbBuildingCommunity} />
        <div className="pft-stat__body">
          <div className="pft-stat__label">Current Units</div>
          <div className="pft-stat__value">{rows.length}</div>
          <div className="pft-stat__sub">{unitsSub}</div>
        </div>
        <div className="pft-stat__occupancy"><span className="pft-pill pft-pill--blue">{t.occupancy}%</span><span>occupied</span></div>
      </div>
      <div
        {...popTile("vacant", "occupied")}
        data-testid="stat-occupancy"
        aria-label={`Occupancy / Current Vacancy: ${t.occupied} occupied, ${t.vacant} vacant${t.rentedVacant ? `, ${t.rentedVacant} already rented` : ""}`}
      >
        <StatIcon icon={TbUsers} />
        <div className="pft-stat__body">
          <div className="pft-stat__label">Occupancy / Vacancy</div>
          <div className="pft-stat__value">
            <Split parts={[t.occupied, t.vacant]} />
            {/* Vacant units with a new lease signed (owner, 2026-10-02): beside the vacancy figure, only when any. */}
            {t.rentedVacant > 0 && <span className="pft-pill" data-testid="stat-occupancy-rented">{t.rentedVacant} rented</span>}
          </div>
          <div className="pft-stat__sub">{t.hasRentedInfo ? `${t.vacant - t.rentedVacant} still to rent` : "vacant today"}</div>
        </div>
        {popover("vacant")}
      </div>
      {/* Leases ending from today to the end of next month (owner, 2026-09-28 window). */}
      <div
        {...popTile("ending", "renewals")}
        data-testid="stat-renewals"
        aria-label={`Renewals till ${win.endLabel}: ${t.expiring} leases ending${t.hasRentedInfo ? `, ${t.rentedExpiring} renewed or rented, ${t.openExpiring} open` : ""}`}
      >
        <StatIcon icon={TbFileSpreadsheet} />
        <div className="pft-stat__body">
          <div className="pft-stat__label">Renewals till {win.endLabel}</div>
          {/* The maths is the figure (owner, 2026-10-02): ending − renewed/re-rented = open, open in red. */}
          {t.hasRentedInfo ? (
            <div className="pft-stat__value pft-stat__value--calc" data-testid="stat-renewals-open">
              {t.expiring} − {t.rentedExpiring} = <span className="pft-open">{t.openExpiring} <span className="pft-pill pft-pill--open">open</span></span>
            </div>
          ) : (
            <>
              <div className="pft-stat__value">{t.expiring}</div>
              <div className="pft-stat__calc">leases ending</div>
            </>
          )}
          <div className="pft-stat__sub">{win.startLabel} – {win.endLabel} · {win.daysToEnd} days</div>
        </div>
        {popover("ending")}
      </div>
      {t.hasRentedInfo && (
        <div
          {...popTile("rented", "rented")}
          data-testid="stat-renewed-rented"
          aria-label={`Renewed / Rented / Pending: ${t.renewedExpiring} renewed, ${t.newRented} rented, ${t.upcomingExpiring} awaiting signatures`}
        >
          <StatIcon icon={TbSignature} />
          <div className="pft-stat__body">
            <div className="pft-stat__label">Renewed / Rented / Pending</div>
            <div className="pft-stat__value"><Split parts={[t.renewedExpiring, t.newRented, t.upcomingExpiring]} /></div>
            <div className="pft-stat__sub">{t.rentedExpiring} signed · {t.upcomingExpiring} awaiting signatures</div>
          </div>
          {popover("rented")}
        </div>
      )}
      <div className="pft-stat pft-stat--rent">
        <StatIcon icon={TbDatabase} />
        <div className="pft-stat__body">
          <div className="pft-stat__label">Basic Rent</div>
          <div className="pft-stat__value">{moneyShort(t.basic)}</div>
          <div className="pft-stat__sub">per month</div>
        </div>
      </div>
      <div className="pft-stat pft-stat--target">
        <StatIcon icon={TbTargetArrow} />
        <div className="pft-stat__body">
          <div className="pft-stat__label">Target Rent</div>
          <div className="pft-stat__value">{moneyShort(t.target)}</div>
          <div className="pft-stat__sub">per month</div>
        </div>
      </div>
      <div className="pft-stat pft-stat--gap">
        <StatIcon icon={TbChartBar} />
        <div className="pft-stat__body">
          <div className="pft-stat__label">Gap to Target</div>
          <div className="pft-stat__value">{moneyShort(t.gap)}</div>
          <div className="pft-stat__sub pft-stat__variance" aria-label={`${t.up} units at or above target, ${t.near} within $75 of target, ${t.down} below target by $75 or more`}>
            <span><span className="pft-stat__trend pft-stat__trend--up">↑</span> {t.up}</span>
            <span><span className="pft-stat__trend pft-stat__trend--near">~</span> {t.near}</span>
            <span><span className="pft-stat__trend pft-stat__trend--down">↓</span> {t.down}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MastersheetStats;
